import React, {
  useState, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Label,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Authentication from 'components/App/Authentication';
import AuthTermsNotice from 'components/App/AuthTermsNotice';
import Meta from 'components/App/Meta';
import images from 'assets/images';

import { checkHadError } from 'utils';
import { loginAdminStep2Validation, loginAdminValidation } from 'utils/validate';
import { LOGIN_TYPE, storageKey } from 'constants/index';
import ModalSellerWarning from 'components/Modals/ModalSellerWarning';

import styles from './Login.module.scss';

const ARRAY_OF_PHONE_LINES = [1, 2, 3, 4, 5, 6];

const LOGIN_STEP = {
  FILL_DATA_STEP: 0,
  VERIFICATION_CODE_STEP: 1,
};

const COUNT_DOWN_TIMER = 10;

function Login(props) {
  const {
    loadingStep1,
    loadingStep2,

    loginError,

    actionLoginIntranet,
    actionLoginExtranet,
  } = props;

  const [isShowPassword, setIsShowPassword] = useState(true);
  const [step, setStep] = useState(LOGIN_STEP.FILL_DATA_STEP);
  const [seconds, setSeconds] = useState(COUNT_DOWN_TIMER);

  const loginTypeFromStorage = useMemo(() => {
    const type = process.env.REACT_APP_LOGIN_TYPE;
    return type === LOGIN_TYPE.EXTRANET ? LOGIN_TYPE.EXTRANET : LOGIN_TYPE.INTRANET;
  }, []);

  const [selectedLoginType, setSelectedLoginType] = useState(loginTypeFromStorage);
  const [isOpenSellerWarning, setIsOpenSellerWarning] = useState(false);

  // Dùng ref để tránh stale closure trong formik onSubmit
  const selectedLoginTypeRef = useRef(loginTypeFromStorage);

  useEffect(() => {
    const type = process.env.REACT_APP_LOGIN_TYPE;
    const targetType = type === LOGIN_TYPE.EXTRANET ? LOGIN_TYPE.EXTRANET : LOGIN_TYPE.INTRANET;
    setSelectedLoginType(targetType);
    selectedLoginTypeRef.current = targetType;

    if (targetType === LOGIN_TYPE.EXTRANET) {
      const isAccepted = localStorage.getItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED);
      if (!isAccepted) {
        setIsOpenSellerWarning(true);
      }
    }
  }, []);

  const handleConfirmSellerWarning = useCallback(() => {
    localStorage.setItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED, 'true');
    setIsOpenSellerWarning(false);
  }, []);

  const handleCloseSellerWarning = useCallback(() => {
    setIsOpenSellerWarning(false);
  }, []);

  const history = useHistory();

  // const handleSelectLoginType = useCallback((type) => {
  //   selectedLoginTypeRef.current = type;
  //   setSelectedLoginType(type);
  // }, []);

  const onCallBackNextStep = useCallback((value) => {
    setStep(value);
  }, []);

  const validationStep1 = useFormik({
    initialValues: {
      email: '',
      password: '',
    },

    validationSchema: Yup.object({
      email: loginAdminValidation.usernameOrEmail,
      password: loginAdminValidation.password,
    }),

    onSubmit: (values) => {
      const payload = {
        ...values,
        history,
        callback: onCallBackNextStep,
      };

      if (selectedLoginTypeRef.current === LOGIN_TYPE.EXTRANET) {
        const isAccepted = localStorage.getItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED);
        if (!isAccepted) {
          setIsOpenSellerWarning(true);
          return;
        }
        actionLoginExtranet(payload);
      } else {
        actionLoginIntranet(payload);
      }
    },
  });

  const validationStep2 = useFormik({
    initialValues: {
      phoneNumber: '',
      code: '',
    },

    validationSchema: Yup.object({
      code: loginAdminStep2Validation.code,
    }),

    onSubmit: () => {
      const payload = {
        email: validationStep1.values.email,
        password: validationStep1.values.password,
        history,
      };

      if (selectedLoginTypeRef.current === LOGIN_TYPE.EXTRANET) {
        const isAccepted = localStorage.getItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED);
        if (!isAccepted) {
          setIsOpenSellerWarning(true);
          return;
        }
        actionLoginExtranet(payload);
      } else {
        actionLoginIntranet(payload);
      }
    },
  });

  const onChangeEmail = useCallback((e) => {
    validationStep1.setValues((prev) => ({
      ...prev,
      email: e.target.value.trim(),
    }), [validationStep1]);
  });

  const onChangePassword = useCallback((e) => {
    if (e.currentTarget.value.includes(' ')) {
      e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
    }
    validationStep1.setValues((prev) => ({
      ...prev,
      password: e.target.value,
    }));
  }, [validationStep1]);

  const onToggleShow = useCallback((e) => {
    e.preventDefault();
    setIsShowPassword((prevState) => !prevState);
  }, []);

  // Chỉ xử lý submit ở đây, KHÔNG dùng thêm onClick trên button để tránh double submit
  const onSubmitStep1 = useCallback((e) => {
    e.preventDefault();
    if (selectedLoginTypeRef.current === LOGIN_TYPE.EXTRANET) {
      const isAccepted = localStorage.getItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED);
      if (!isAccepted) {
        setIsOpenSellerWarning(true);
        return;
      }
    }
    validationStep1.submitForm();
  }, [validationStep1]);

  const onSubmitStep2 = useCallback((e) => {
    e.preventDefault();
    if (selectedLoginTypeRef.current === LOGIN_TYPE.EXTRANET) {
      const isAccepted = localStorage.getItem(storageKey.EXTRANET_SELLER_WARNING_ACCEPTED);
      if (!isAccepted) {
        setIsOpenSellerWarning(true);
        return;
      }
    }
    validationStep2.handleSubmit();
  }, [validationStep2]);

  const validationErrorsStep2 = useMemo(() => validationStep2.errors, [validationStep2.errors]);

  const onRecode = useCallback(() => {
    setSeconds(COUNT_DOWN_TIMER);
    // TODO: _____Đoạn code cần sử dụng ở phiên bản tiếp theo_____
  }, [
    validationStep1,
    onCallBackNextStep,
  ]);

  const renderStep = useCallback(() => {
    switch (step) {
      case LOGIN_STEP.VERIFICATION_CODE_STEP: {
        return (
          <div className="tab-content">
            <form
              onSubmit={onSubmitStep2}
              className="tab-pane active"
              id="sign-in"
            >
              <div className="d-flex justify-content-start">
                <div className="form-input__phone-code position-relative">
                  <input
                    type="text"
                    className={`phone-input ${validationStep2.values.code.toString().length > 5 && styles.HiddenCaret}`}
                    name="code"
                    maxLength={6}
                    value={validationStep2.values.code}
                    onChange={validationStep2.handleChange}
                    onBlur={validationStep2.handleBlur}
                    disabled={loadingStep2}
                  />

                  <div className="phone-line">
                    {ARRAY_OF_PHONE_LINES.map((index) => <div className="phone-line__item" key={index} />)}
                  </div>

                  {checkHadError(validationStep2, 'code') && (
                  <span className="form-input--error">
                    {validationErrorsStep2.code}
                  </span>
                  )}
                </div>
              </div>

              <p className="mb-5 mt-4">
                { seconds === 0
                  ? (
                    <>
                      <span className="me-2">
                        Chưa nhận được mã OTP?
                      </span>

                      <AppButton
                        type="button"
                        classes="btn_recode text-underline"
                        onClick={onRecode}
                      >
                        GỬI LẠI
                      </AppButton>
                    </>
                  )
                  : `Mã xác thực có hiệu lực trong ${seconds}s`}
              </p>

              <AppButton
                type="submit"
                classes="btn w-100"
                loading={loadingStep2}
                disabled={checkHadError(validationStep2, 'code')}
              >
                Tiếp theo
              </AppButton>
            </form>
          </div>
        );
      }

      default: {
        return (
          // onSubmit trên form xử lý submit — button KHÔNG cần onClick
          <form onSubmit={onSubmitStep1}>
            <div className="Login__Email">
              <Label className="Login__Label">
                Tên đăng nhập / Email
                <code>*</code>
              </Label>

              <AppInputValidation
                inputClassName="Login__App-Input"
                placeholder="Nhập tên đăng nhập"
                name="email"
                validation={validationStep1}
                isCustomOnChange
                onChange={onChangeEmail}
              />
            </div>

            <div className="Login__Password">
              <Label className="Login__Label">
                Mật khẩu
                <code>*</code>
              </Label>

              <AppInputValidation
                inputClassName="Login__App-Input"
                placeholder="Nhập mật khẩu"
                name="password"
                validation={validationStep1}
                type={isShowPassword ? 'password' : 'text'}
                isCustomOnChange
                onChange={onChangePassword}
              />

              <i
                className={`font-size-18 fas
                  ${checkHadError(validationStep1, 'password') ? 'eye-error' : 'eye-default'}
                  ${!isShowPassword ? 'fa-eye' : 'fa-eye-slash'} cursor-pointer Login__Show-Pass-Icon
                  `}
                aria-hidden="true"
                onMouseDown={onToggleShow}
              />
            </div>

            <Link
              to="/forgot-password"
              className="Login__Forgot-Password"
            >
              Quên mật khẩu?
            </Link>

            <AppButton
              classes="Login__Btn-Login"
              loading={loadingStep1}
              type="submit"
            >
              ĐĂNG NHẬP
            </AppButton>

            {/* {selectedLoginType === LOGIN_TYPE.EXTRANET && ( */}
            <AuthTermsNotice className={styles.ExtranetTerms} />
            {/* )} */}
          </form>
        );
      }
    }
  }, [
    seconds,
    step,
    selectedLoginType,
    validationErrorsStep2,
    validationStep1,
    validationStep2,
    onRecode,
    onSubmitStep1,
    onSubmitStep2,
  ]);

  useEffect(() => {
    if (step === LOGIN_STEP.VERIFICATION_CODE_STEP) {
      const myInterval = setInterval(() => {
        if (seconds <= 0) {
          clearInterval(myInterval);
        } else {
          setSeconds((prevState) => prevState - 1);
        }
      }, 1000);
      return () => {
        clearInterval(myInterval);
      };
    }

    return setSeconds(COUNT_DOWN_TIMER);
  }, [step, seconds]);

  useEffect(() => {
    if (Object.keys(loginError).length) {
      validationStep1.setErrors({
        ...loginError,
      });
    }
  }, [loginError]);

  if (selectedLoginType === LOGIN_TYPE.EXTRANET) {
    return (
      <div className={styles.ExtranetContainer}>
        <Meta name="Đăng Nhập" />

        <header className={styles.ExtranetHeader}>
          <div className={styles.HeaderContent}>
            <img
              src={images.logoBookeseFull.default}
              alt="logo-admin-login"
              className={styles.HeaderLogo}
            />
          </div>
        </header>

        <main className={styles.ExtranetMain}>
          <div className={styles.ExtranetCard}>
            <div className={styles.CardHeader}>
              <h1 className={styles.CardTitle}>Xin chào!</h1>
              <p className={styles.CardDescription}>
                {step === LOGIN_STEP.FILL_DATA_STEP
                  ? 'Đăng nhập để đến quản lý chỗ nghỉ'
                  : 'Chúng tôi đã gửi mã OTP đến Email Quý vị'}
              </p>
            </div>

            <div className={styles.CardBody}>
              <div className={styles.Page}>
                {renderStep()}
              </div>
            </div>
          </div>
        </main>

        <footer className={styles.ExtranetFooter}>
          <p className={styles.Copyright}>
            © 2022 Bookese. Design and Develop by Wiicamp
          </p>
        </footer>

        <ModalSellerWarning
          isOpen={isOpenSellerWarning}
          onCloseModal={handleCloseSellerWarning}
          onConfirm={handleConfirmSellerWarning}
        />
      </div>
    );
  }

  return (
    <>
      <Meta name="Đăng Nhập" />

      <Authentication
        title="Xin chào!"
        description={step === LOGIN_STEP.FILL_DATA_STEP ? 'Đăng nhập để đến quản lý chỗ nghỉ' : 'Chúng tôi đã gửi mã OTP đến Email Quý vị'}
      >
        <div className={styles.Page}>
          {renderStep()}
        </div>
      </Authentication>
    </>
  );
}

Login.propTypes = {
  loadingStep1: PropTypes.bool.isRequired,
  loadingStep2: PropTypes.bool.isRequired,

  loginError: PropTypes.instanceOf(Object),

  actionLoginIntranet: PropTypes.func.isRequired,
  actionLoginExtranet: PropTypes.func.isRequired,
};

Login.defaultProps = {
  loginError: {},
};

export default Login;
